import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import "./ReceiptTemplate.css";

const ReceiptTemplate = ({ data }) => {
  const receiptRef = useRef();
  const [templateConfig, setTemplateConfig] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get("/api/templates/active/invoice"); // Sharing invoice/receipt category if needed, or use 'receipt'
        // User screenshots show "Invoice & Receipt Templates" as one category.
        // Let's check if there is a separate 'receipt' category or if it uses 'invoice'.
        // In TemplatesList, it used 'invoice' for "Invoice & Receipt Templates".
        if (res.data) {
          setTemplateConfig(res.data.config || {});
        }
      } catch (err) {
        console.error("Failed to load active template:", err);
      }
    };
    fetchTemplate();
  }, []);

  const handleDownloadPdf = () => {
    const input = receiptRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Receipt_${data?.receipt_number || "REC"}.pdf`);
    });
  };

  if (!data) return <div className="p-4 text-center">No receipt data provided.</div>;

  const config = templateConfig || {};
  const areaStyles = config.areaStyles || [];
  
  const getStyle = (areaName) => {
    const area = areaStyles.find(a => a.area === areaName);
    if (!area) return {};
    return {
      backgroundColor: area.bgColor,
      color: area.fontColor,
      fontSize: `${area.fontSize}px`,
      fontFamily: config.defaultFont || "Arial",
    };
  };

  const globalStyle = {
    fontFamily: config.defaultFont || "Arial",
    color: config.defaultFontColor || "#000",
    fontSize: `${config.defaultFontSize || 12}px`,
    lineHeight: "1.4",
  };

  return (
    <div className="receipt-viewer p-4">
      <div className="flex justify-end mb-4 no-print">
        <button className="btn btn-primary" onClick={handleDownloadPdf}>
          📄 Download PDF
        </button>
      </div>

      <div className="receipt-wrapper" style={{background: "#e0e0e0", padding: "40px", display: "flex", justifyContent: "center"}}>
        <div 
          className="receipt-container shadow-lg" 
          ref={receiptRef} 
          style={{
            ...globalStyle, 
            width: "210mm", 
            minHeight: "297mm", 
            backgroundColor: "#fff", 
            padding: "15mm",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column"
          }}
        >

          {/* HEADER SECTION */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-4 items-center">
              {config.logoUrl ? (
                <img src={config.logoUrl} alt="Logo" style={{maxHeight: 70}} crossOrigin="anonymous"/>
              ) : (
                <img src={process.env.PUBLIC_URL + "/rekonic-logo.jpg"} alt="Rekonic Logo" style={{maxHeight: 60}} />
              )}
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/IATA_logo.svg/320px-IATA_logo.svg.png" alt="IATA" style={{maxHeight: 50}} />
            </div>

            <div className="text-right flex flex-col items-end">
              <div className="qr-code mb-2" style={{width: 80, height: 80, border: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "center"}}>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${data.receipt_number}`} alt="QR" />
              </div>
              <div className="text-[10px] leading-tight">
                <p className="font-bold text-[#800000]">RECEIPT#: {data.receipt_number}</p>
                <p className="font-bold text-[#800000]">{data.receipt_number} :رقم الإيصال</p>
                <p className="mt-1">TRN#: 100519322000003</p>
                <p>100519322000003 :رقم التسجيل الضريبي</p>
                <p className="mt-1">STAFF#: {data.created_by}</p>
                <p>{data.created_by} :الموظف</p>
              </div>
            </div>
          </div>

          {/* GREETINGS */}
          <div className="mb-4 text-[11px] leading-relaxed text-blue-900 font-medium">
            <p>Dear Valued Customer,</p>
            <p>Greetings from REKONIC TRAVEL LLC -</p>
            <p>(ريكونيك ترافيل ش م ح) .</p>
          </div>

          {/* TITLE */}
          <div className="text-center mb-6 py-1 border-y-2 border-gray-100">
            <h2 className="text-lg font-bold text-black uppercase m-0">RECEIPT - إيصال</h2>
          </div>

          {/* CUSTOMER & SUMMARY BOX */}
          <div className="flex justify-between mb-6">
            <div className="customer-box flex-1">
              <h3 className="text-sm font-bold text-blue-900 mb-1 uppercase">{data.customer_name || data.supplier_name || "N/A"}</h3>
              <div className="text-[11px] text-gray-700 leading-tight">
                <p>{data.party_type === "Customer" ? "SHARJAH" : "SUPPLIER"}</p>
                <p>{data.trn ? `TRN: ${data.trn}` : ""}</p>
                <p>ACC#: {data.customer_name || data.supplier_name}</p>
                {data.sales_id && <p>SALES ID: {data.sales_id}</p>}
              </div>
            </div>

            <div className="summary-box w-[45%]">
              <table className="w-full border-collapse border border-blue-900 text-[10px]">
                <thead>
                  <tr className="bg-[#002040] text-white">
                    <th className="border border-blue-900 p-1 text-left">RECEIPT DATE<br/>تاريخ الإيصال</th>
                    <th className="border border-blue-900 p-1 text-center bg-[#002040]">AMOUNT<br/>المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center h-10">
                    <td className="border border-blue-900 p-1">{data.payment_date ? new Date(data.payment_date).toLocaleDateString() : "N/A"}</td>
                    <td className="border border-blue-900 p-1 bg-[#002040] text-white font-bold text-sm">AED {(Number(data.amount_paid) || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* SERVICES DETAILS */}
          <div className="mb-4">
             <h4 className="text-[10px] font-bold uppercase text-gray-600 mb-1">SERVICES DETAILS - تفاصيل الخدمات</h4>
             <table className="w-full border-collapse border border-blue-900 text-[10px]">
                <thead className="bg-[#002040] text-white">
                   <tr>
                      <th className="border border-blue-900 p-1 text-left">DETAILS<br/>التفاصيل</th>
                      <th className="border border-blue-900 p-1 text-right w-16">AMOUNT<br/>المبلغ</th>
                      <th className="border border-blue-900 p-1 text-right w-16">TAX %<br/>نسبة الضريبة</th>
                      <th className="border border-blue-900 p-1 text-right w-20">TAX AMOUNT<br/>مبلغ الضريبة</th>
                      <th className="border border-blue-900 p-1 text-right w-20">TOTAL<br/>الإجمالي</th>
                   </tr>
                </thead>
                <tbody>
                   <tr className="align-top">
                      <td className="border border-blue-900 p-2 italic whitespace-pre-wrap">
                        {data.remarks || "Payment Received"}
                        {data.bank_name && `\nBank: ${data.bank_name}`}
                        {data.cheque_number && `\nCheque No: ${data.cheque_number} (Date: ${data.cheque_date})`}
                      </td>
                      <td className="border border-blue-900 p-2 text-right">{(Number(data.amount_paid) || 0).toFixed(2)}</td>
                      <td className="border border-blue-900 p-2 text-right">{(Number(data.tax_percentage) || 0).toFixed(2)}%</td>
                      <td className="border border-blue-900 p-2 text-right">{(Number(data.tax_amount) || 0).toFixed(2)}</td>
                      <td className="border border-blue-900 p-2 text-right font-bold">{(Number(data.amount_paid) + Number(data.tax_amount) || 0).toFixed(2)}</td>
                   </tr>
                </tbody>
             </table>
             <div className="flex flex-col items-end mt-1 text-[10px] font-bold text-gray-800">
                <p>TOTAL TAX (AED): {(Number(data.tax_amount) || 0).toFixed(2)}</p>
                <p className="text-blue-900">GRAND TOTAL (AED): {(Number(data.amount_paid) + Number(data.tax_amount) || 0).toFixed(2)}</p>
             </div>
          </div>

          {/* PAYMENT DETAILS */}
          <div className="mb-6">
             <h4 className="text-[10px] font-bold uppercase text-gray-600 mb-1 text-center">PAYMENT DETAILS - تفاصيل الدفع</h4>
             <table className="w-full border-collapse border border-blue-900 text-[10px]">
                <thead className="bg-[#002040] text-white uppercase">
                   <tr>
                      <th className="border border-blue-900 p-1 w-8">NO<br/>الرقم</th>
                      <th className="border border-blue-900 p-1 text-left">RECEIPT TYPE<br/>نوع الإيصال</th>
                      <th className="border border-blue-900 p-1 text-left">PAYMENT MODE<br/>طريقة الدفع</th>
                      <th className="border border-blue-900 p-1 text-left">DESCRIPTION<br/>الوصف</th>
                      <th className="border border-blue-900 p-1 text-right w-20">TOTAL<br/>الإجمالي</th>
                   </tr>
                </thead>
                <tbody>
                    <tr>
                         <td className="border border-blue-900 p-2 text-center">1</td>
                         <td className="border border-blue-900 p-2">Standard Receipt</td>
                         <td className="border border-blue-900 p-2 uppercase">{data.payment_mode}</td>
                         <td className="border border-blue-900 p-2">
                           {data.bank_name ? `Bank: ${data.bank_name}` : ""}
                           {data.cheque_number ? `Cheque: ${data.cheque_number}` : ""}
                         </td>
                         <td className="border border-blue-900 p-2 text-right font-bold">{(Number(data.amount_paid) || 0).toFixed(2)}</td>
                    </tr>
                   {/* Remaining Balance Row */}
                   <tr className="bg-red-50 font-bold text-red-700">
                      <td colSpan="4" className="border border-blue-900 p-2 text-right uppercase">Remaining Balance (AED)</td>
                      <td className="border border-blue-900 p-2 text-right">{(Number(data.balance_remaining) || 0).toFixed(2)}</td>
                   </tr>
                </tbody>
             </table>
             <div className="flex flex-col items-end mt-1 text-[10px] font-bold text-blue-900">
                <p>TOTAL AMOUNT PAID (AED): {(Number(data.amount_paid) || 0).toFixed(2)}</p>
             </div>
          </div>

          {/* ASSETS SECTION (Stamp & Sign) */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex gap-4">
               <div className="text-center">
                  {config.addSignature !== false && config.signatureUrl ? (
                    <img src={config.signatureUrl} alt="Signature" style={{maxHeight: 70}} crossOrigin="anonymous"/>
                  ) : (
                    <div className="h-16 w-32 border-b-2 border-gray-200"></div>
                  )}
                  <div className="mt-2 text-[10px] font-bold text-blue-900 uppercase">Authorised Signature<br/><span className="text-gray-400 font-normal">التوقيع المعتمد</span></div>
               </div>
               
               {config.addStamp !== false && config.stampUrl && (
                  <img src={config.stampUrl} alt="Stamp" style={{maxHeight: 100}} crossOrigin="anonymous"/>
               )}
            </div>

            <div className="company-contact text-right text-[10px] text-blue-900 leading-tight">
               <p className="font-bold">REKONIC TRAVEL LLC</p>
               <p>Sharjah, United Arab Emirates</p>
               <p>Support: info@rekonic.com</p>
               <p>Phone: +971 XXXXXXXX</p>
            </div>
          </div>

          {/* BANK ACCOUNTS SECTION */}
          <div className="mt-auto">
            <table className="w-full border-collapse border border-blue-900 text-[9px]">
              <thead>
                <tr className="bg-[#002040] text-white">
                  <th colSpan="2" className="border border-blue-900 p-1 text-center">BANK ACCOUNT INFORMATION - بيانات الحساب البنكي</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50 w-1/4">Account Title</td>
                  <td className="border border-blue-900 p-1 uppercase font-bold">REKONIC TRAVEL LLC</td>
                </tr>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50">Account Number</td>
                  <td className="border border-blue-900 p-1 font-bold">010XXXXXXXXXX</td>
                </tr>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50">Bank Name</td>
                  <td className="border border-blue-900 p-1 font-bold uppercase">MASHREQ BANK</td>
                </tr>
                <tr>
                  <td className="border border-blue-900 p-1 bg-gray-50">Branch Name & Address</td>
                  <td className="border border-blue-900 p-1 font-bold uppercase">SHARJAH, UAE</td>
                </tr>
              </tbody>
            </table>
            
            <div className="mt-4 text-[9px] text-blue-900 leading-tight italic">
               <p>This is a "Computer Generated Document" from REKONIC TRAVEL LLC. and does not require any official signature.</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ReceiptTemplate;
